// @ts-check

import { countEnabledColumns } from '../countEnabledColumns';

/** @typedef {import('../state').ExportTemplateState} ExportTemplateState */

/**
 * Holds the actions specific to modifying columns within a given sheet.
 * The "sheet" becomes a draft while modifying to allow the user to cancel
 * the editing of the sheet columns and revert back to their previous state.
 */

/**
 * User started editing columns for a specific sheet.
 * Creates a deep copy of the sheet to allow cancel without losing changes.
 * @param {ExportTemplateState} state
 * @param {string} payload
 */
export const columnEditingStarted = (state, payload) => {
  const sourceSheet = state.edit.sheetMap[payload];
  const draftSheet = {
    ...sourceSheet,
    columnMap: { ...sourceSheet.columnMap },
    columnOrder: [...sourceSheet.columnOrder],
  };

  for (const colId of Object.keys(draftSheet.columnMap)) {
    draftSheet.columnMap[colId] = { ...sourceSheet.columnMap[colId] };
  }

  return {
    ...state,
    edit: {
      ...state.edit,
      view: 'editColumns',
      draftSheetId: payload,
      draftSheet,
    },
  };
};

/**
 * User confirmed column edits and returned to sheet view.
 * Applies the draft sheet back to the sheetMap.
 * @param {ExportTemplateState} state
 */
export const columnEditingConfirmed = state => {
  const { draftSheetId, draftSheet } = state.edit;
  const hasDraft = draftSheetId && draftSheet;

  return {
    ...state,
    edit: {
      ...state.edit,
      view: 'edit',
      draftSheetId: null,
      draftSheet: null,
      sheetMap: hasDraft
        ? { ...state.edit.sheetMap, [draftSheetId]: draftSheet }
        : state.edit.sheetMap,
    },
  };
};

/**
 * User cancelled column edits and returned to sheet view.
 * Discards the draft sheet without applying changes.
 * @param {ExportTemplateState} state
 */
export const columnEditingCancelled = state => ({
  ...state,
  edit: {
    ...state.edit,
    view: 'edit',
    draftSheetId: null,
    draftSheet: null,
  },
});

/**
 * A column was toggled on/off in the editing template.
 * Operates on draftSheet when in column editing mode.
 * @param {ExportTemplateState} state
 * @param {{ sheetId: string, columnId: string }} payload
 */
export const editingColumnEnableToggled = (state, payload) => {
  if (!state.edit.draftSheet) {
    return state;
  }

  const source = state.edit.draftSheet.columnMap[payload.columnId];
  const column = {
    ...source,
    enabled: source.mandatory ? true : !source.enabled,
  };

  const draftSheet = {
    ...state.edit.draftSheet,
    columnMap: {
      ...state.edit.draftSheet.columnMap,
      [payload.columnId]: column,
    },
  };
  draftSheet.selectedColumns = countEnabledColumns(draftSheet);

  return {
    ...state,
    edit: { ...state.edit, draftSheet },
  };
};

/**
 * A column was renamed in the editing template.
 * Operates on draftSheet when in column editing mode.
 * @param {ExportTemplateState} state
 * @param {{ sheetId: string, columnId: string, overridenName: string | null }} payload
 */
export const editingColumnNameChanged = (state, payload) => {
  const { draftSheet } = state.edit;

  if (draftSheet) {
    const updatedColumn = {
      ...draftSheet.columnMap[payload.columnId],
      overridenName: payload.overridenName,
    };
    return {
      ...state,
      edit: {
        ...state.edit,
        draftSheet: {
          ...draftSheet,
          columnMap: {
            ...draftSheet.columnMap,
            [payload.columnId]: updatedColumn,
          },
        },
      },
    };
  }

  const sheet = state.edit.sheetMap[payload.sheetId];
  const updatedColumn = {
    ...sheet.columnMap[payload.columnId],
    overridenName: payload.overridenName,
  };
  return {
    ...state,
    edit: {
      ...state.edit,
      sheetMap: {
        ...state.edit.sheetMap,
        [payload.sheetId]: {
          ...sheet,
          columnMap: { ...sheet.columnMap, [payload.columnId]: updatedColumn },
        },
      },
    },
  };
};

/**
 * Set all non-mandatory columns in a sheet to enabled/disabled.
 * Operates on draftSheet when in column editing mode.
 * @param {ExportTemplateState} state
 * @param {{ sheetId: string, enabledValue: boolean }} payload
 */
export const editingSheetEnableAllSet = (state, payload) => {
  const { draftSheet } = state.edit;
  const sheet = draftSheet || state.edit.sheetMap[payload.sheetId];

  /** @type {Record<string, import('../state').EditingColumn>} */
  const updatedColumnMap = {};
  for (const [id, column] of Object.entries(sheet.columnMap)) {
    // Unavailable columns are hidden from the user; leave their persisted
    // enabled state alone rather than silently overwriting it.
    if (column.available === false) {
      updatedColumnMap[id] = column;
      continue;
    }
    updatedColumnMap[id] = {
      ...column,
      enabled: column.mandatory ? true : payload.enabledValue,
    };
  }

  /** @type {import('../state').EditingSheet} */
  const updatedSheet = { ...sheet, columnMap: updatedColumnMap };
  updatedSheet.selectedColumns = countEnabledColumns(updatedSheet);

  if (draftSheet) {
    return { ...state, edit: { ...state.edit, draftSheet: updatedSheet } };
  }
  return {
    ...state,
    edit: {
      ...state.edit,
      sheetMap: { ...state.edit.sheetMap, [payload.sheetId]: updatedSheet },
    },
  };
};
